import { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import axios from 'axios';
import { BounceLoader } from 'react-spinners';
import { useSession } from 'next-auth/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faChevronLeft, faChevronRight, faXmark } from '@fortawesome/free-solid-svg-icons';

import errorMsg from '../../../../../Functions/errorMsg';
import successMsg from '../../../../../Functions/successMsg';

import DropMenu from '../../../../../FunctionalComponents/CustomDropMenu/DropMenu';

export default function Overview({ openCreateOrder, openEditOrder, viewOrder, ordersToUpdate, removeUpdatedOrder  }) {
  const { data: session, status } = useSession();

  const [hasError, setHasError] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [ordersData, setOrdersData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [confirmArchive, setConfirmArchive] = useState(null); // State to handle archive confirmation
  const [loadingArchive, setLoadingArchive] = useState(false); // State for archive loading spinner

  useEffect(() => {
    if (status !== 'authenticated' || loadingData || ordersData) return;

    async function fetchOrdersData() {
      setLoadingData(true);
      try {
        const getResult = await axios.get(`${window.location.origin}/api/vendor/manage/orders/ordersData`);

        if (getResult && getResult.status === 200) {
          setOrdersData(getResult.data);
        }
      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
        setHasError(true);
      } finally {
        setLoadingData(false);
      }
    }

    fetchOrdersData();

  }, [session, status, hasError, loadingData, ordersData]);

  useEffect(() => {
    if(!ordersData || !session || status !== 'authenticated') return;

    if (ordersToUpdate.length > 0 && ordersData) {
      const updatedOrdersData = ordersData.map((order) => {
        const update = ordersToUpdate.find((update) => update.orderID === order.orderID);
        if (update) {
          return { ...order, status: update.status };
        }
        return order;
      });

      setOrdersData(updatedOrdersData);

      ordersToUpdate.forEach((update) => removeUpdatedOrder(update.orderID));
    }

  }, [session, status, ordersData, ordersToUpdate, removeUpdatedOrder]);

  const maxPages = ordersData ? Math.ceil(ordersData.length / itemsPerPage) : 1;

  const handlePageChange = (values) => {
    const value = parseInt(values.value);
    if (!isNaN(value) && value >= 1 && value <= maxPages) {
      setCurrentPage(value);
    }
  };

  const isAllowed = (values) => {
    const { floatValue } = values;
    return floatValue >= 1 && floatValue <= maxPages;
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < maxPages) setCurrentPage(currentPage + 1);
  };

  function onChangeItemsPerPage(num) {
    setItemsPerPage(parseInt(num));
    setCurrentPage(1); // Reset to the first page when items per page changes
  }

  const confirmArchiveOrder = (orderID) => {
    setConfirmArchive(orderID); // Set the order ID to confirm deletion
  };

  const handleArchiveOrder = async () => {
    if (!confirmArchive) return;
  
    setLoadingArchive(true);
    try {
      const result = await axios.post(`${window.location.origin}/api/vendor/manage/orders/handleArchive`, {
        orderID: confirmArchive,
      });
  
      if (result && result.status === 200) {
        const { isArchived } = result.data;
  
        successMsg(`Order ${isArchived ? 'archived' : 'unarchived'} successfully.`);
  
        // Update isArchived on the front-end
        setOrdersData((prevOrders) =>
          prevOrders.map((order) =>
            order.orderID === confirmArchive ? { ...order, isArchived } : order
          )
        );
      }
    } catch (error) {
      errorMsg(error?.response?.data?.status || error.toString());
    } finally {
      setLoadingArchive(false);
      setConfirmArchive(null);
    }
  };  

  const handleCancelArchive = () => {
    setConfirmArchive(null); // Close the confirmation modal
  };

  if (loadingData || !ordersData || !session || status !== 'authenticated' || loadingArchive || hasError) {
    return (
      <section id={styles.loading}>
        <BounceLoader size={75} speedMultiplier={2} color="#154734" />
      </section>
    );
  }

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = ordersData.slice(startIndex, startIndex + itemsPerPage);

  return (
    <section id={styles.wrapper}>
      <section id={styles.listings}>
        <nav id={styles.pageNav}>
          <section>
            <FontAwesomeIcon
              icon={faChevronLeft}
              id={styles.navIcon}
              onClick={handlePreviousPage}
            />
            <section>
              <h1>{currentPage} / {maxPages}</h1>
            </section>
            <FontAwesomeIcon
              icon={faChevronRight}
              id={styles.navIcon}
              onClick={handleNextPage}
            />
          </section>
          <section>
            <h1>Per Page: </h1>
            <BusinessCategoryDropMenu 
              numbers={[ '12', '24', '36', '48' ]}
              selected={itemsPerPage}
              onChangeItemsPerPage={onChangeItemsPerPage} 
            />
          </section>
        </nav>
        
        <section id={styles.orderList}>

        {confirmArchive && (
        <ArchiveConfirmationModal 
          onCancel={handleCancelArchive} 
          onConfirm={handleArchiveOrder} 
        />
        )}

        <OrderInfoContainer />
          { currentItems.map((order, index) => {
            return (
              <div key={index} style={{ display: 'flex', flexDirection: 'column' }}>
                <OrderCard key={index} data={order} openEditOrder={openEditOrder} confirmArchiveOrder={confirmArchiveOrder} openViewOrder={viewOrder} />
                <hr className={styles.lineSeparator} />
              </div>
            )
          }) }
        </section>
      </section>
      <section id={styles.filters}>
        <div>
          <h1></h1>
        </div>
        <div>
          
        </div>
      </section>
    </section>
  );
}

const OrderInfoContainer = () => {
  const [isChecked, setIsChecked] = useState(false);

  function handleAllSelected() {
    setIsChecked(!isChecked);
  }

  return (
    <section className={styles.orderInfoContainer}>
      <label className={styles.customCheckbox}>
        <input 
          type="checkbox" 
          checked={isChecked} 
          onChange={handleAllSelected} 
        />
        {isChecked && (
          <FontAwesomeIcon icon={faCheck} className={styles.icon} />
        )}
      </label>
      <h1>Name</h1>
      <h1>Type</h1>
      <h1>Amount ($)</h1>
      <h1>Created</h1>
      <h1>Status</h1>
      <h1>Actions</h1>
    </section>
  );
};

const OrderCard = ({ data, openViewOrder, openEditOrder, confirmArchiveOrder }) => {
  const orderID = data.orderID;

  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  function returnStatusClassType() {
    if (data.isArchived) return styles.archived;
  
    const statusClassMap = {
      Processing: styles.processing,
      Ready: styles.ready,
      Shipped: styles.shipped,
      Approved: styles.approved,
      Pending: styles.pending,
      Complete: styles.completed,
      Cancelled: styles.cancelled,
    };
  
    return statusClassMap[data.status] || ''; // Return matching class or empty string
  }
  

  const orderActions = ['View', (data.isArchived || ["Cancelled", "Complete"].includes(data.status)) ? (data.isArchived ? "Unarchive" : "Archive") : null, ].filter(Boolean);


  function handleOrderAction(optionSelected) {
    if (optionSelected === 'View') return openViewOrder(orderID);
    if (optionSelected === 'Archive' && !["Cancelled", "Complete"].includes(data.status)) {
        return errorMsg("Cannot archive orders that are not 'Cancelled' or 'Complete'.");
    }
    if (optionSelected === 'Archive') return confirmArchiveOrder(orderID, true); // Archive
    if (optionSelected === 'Unarchive') return confirmArchiveOrder(orderID, false); // Unarchive
    errorMsg("Invalid action.");
}

  
  

  const orderType = data.orderType.charAt(0).toUpperCase() + data.orderType.slice(1)

  return (
    <section className={styles.orderItem}>
      <label className={styles.customCheckbox}>
        <input 
          type="checkbox" 
          checked={isChecked} 
          onChange={handleCheckboxChange} 
        />
        {isChecked && (
          <FontAwesomeIcon icon={faCheck} className={styles.icon} />
        )}
      </label>
      <h1>{data.customerName || 'Unknown'}</h1>
      <h1>{orderType}</h1>
      <h1>{data.amount}</h1>
      <h1>{data.createdAt}</h1>
      <section id={styles.statusContainer}>
        <h1 className={returnStatusClassType()}> {data.isArchived ? 'Archived' : data.status}</h1>
      </section>
      <section id={styles.actionBar}>
        <DropMenu options={orderActions} placeholder='Action' onMenuItemClick={handleOrderAction} shouldChangePlaceholder={false} />
      </section>
    </section>
  );
};



const BusinessCategoryDropMenu = ({ numbers, selected, onChangeItemsPerPage }) => {
  const [navDropMenu, setNavDropMenu] = useState(false);
    
  const handleSelection = (e, num) => {
    e.preventDefault();
    onChangeItemsPerPage(parseInt(num));
    setNavDropMenu(false);
  };

  return (
    <div onMouseLeave={() => setNavDropMenu(false)} id={styles.categoryDrop}>
      <button 
        onClick={(e) => { e.preventDefault(); setNavDropMenu(!navDropMenu); }} 
        className={styles.dropdownBtn}
      >
        <FontAwesomeIcon icon={faChevronDown} className={styles.icon} /> 
        {selected}
      </button>
      <div style={{ display: navDropMenu ? 'block' : 'none' }} className={styles.dropdownContent}>
        {numbers.map((num, index) => (
          <button key={index} onClick={(e) => handleSelection(e, num)}>
            {num}
          </button>
        ))}
      </div>
    </div>
  );
};

const ArchiveConfirmationModal = ({ onCancel, onConfirm }) => {
  return (
    <section id={styles.confirmArchive}>
      <div>
          <div>
              <h1>Confirm Archive</h1>
              <span>
                  <button id={styles.confirm} onClick={onConfirm}>
                      <FontAwesomeIcon icon={faCheck} id={styles.icon} />
                  </button>
                  <button id={styles.cancel} onClick={onCancel}>
                      <FontAwesomeIcon icon={faXmark} id={styles.icon} />
                  </button>
              </span>
          </div>
      </div>
    </section>
  );
};